/**
 * Live Game Screen Component
 * VOY-11: Simulates a live quiz game
 */

import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Clock, Flame } from 'lucide-react';
import { Question, Booster, calculateScore } from '@/data/gaming-mockup';

interface GameLiveProps {
  questions: Question[];
  boosters: Booster[];
  onComplete: (finalScore: number) => void;
}

export function GameLive({ questions, boosters, onComplete }: GameLiveProps) {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [timeLeft, setTimeLeft] = useState(60);
  const [isAnswered, setIsAnswered] = useState(false);
  const [usedBoosters, setUsedBoosters] = useState<string[]>([]);

  const currentQuestion = questions[currentQuestionIndex];
  const totalQuestions = questions.length;

  const handleAnswer = useCallback((answer: string) => {
    if (isAnswered) return;
    
    setSelectedAnswer(answer);
    setIsAnswered(true);

    const isCorrect = answer === currentQuestion.correct;
    const questionScore = calculateScore(60 - timeLeft, isCorrect);
    
    if (isCorrect) {
      setScore(prevScore => prevScore + questionScore);
      setStreak(prevStreak => prevStreak + 1);
    } else {
      setStreak(0);
    }

    // Move to next question after 2s
    setTimeout(() => {
      if (currentQuestionIndex + 1 < totalQuestions) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
        setSelectedAnswer(null);
        setIsAnswered(false);
        setTimeLeft(60);
      } else {
        // Game complete
        onComplete(score + questionScore);
      }
    }, 2000);
  }, [isAnswered, currentQuestion, timeLeft, currentQuestionIndex, totalQuestions, score, onComplete]);

  // Timer countdown
  useEffect(() => {
    if (timeLeft > 0 && !isAnswered) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && !isAnswered) {
      // Time's up
      handleAnswer('');
    }
  }, [timeLeft, isAnswered, handleAnswer]);

  const handleBooster = (boosterId: string) => {
    if (usedBoosters.includes(boosterId)) return;
    
    setUsedBoosters([...usedBoosters, boosterId]);
    alert(`Demo: ${boosterId} activated!\n\nIn real game, this would apply the booster effect.`);
  };

  const getAnswerClass = (option: string) => {
    if (!isAnswered) {
      return 'border-gray-300 dark:border-zinc-600 hover:border-[#5B6FED] dark:hover:border-blue-500 hover:bg-[#E8E8FF] dark:hover:bg-zinc-700 cursor-pointer';
    }
    
    if (option === currentQuestion.correct) {
      return 'border-green-500 bg-green-50 dark:bg-green-900/30';
    }
    
    if (option === selectedAnswer && option !== currentQuestion.correct) {
      return 'border-red-500 bg-red-50 dark:bg-red-900/30';
    }
    
    return 'border-gray-200 dark:border-zinc-700 opacity-50';
  };

  return (
    <div className="space-y-4">
      {/* Game Header */}
      <Card className="bg-gradient-to-r from-[#5B6FED] to-[#8B9FED] text-white border-0">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <p className="text-sm text-white/80">Question</p>
                <p className="text-2xl font-bold">{currentQuestionIndex + 1}/{totalQuestions}</p>
              </div>
              <div>
                <p className="text-sm text-white/80">Score</p>
                <p className="text-2xl font-bold">{score.toLocaleString()}</p>
              </div>
              {streak > 0 && (
                <div className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-full">
                  <Flame className="w-4 h-4 text-orange-300" />
                  <span className="font-bold">{streak}</span>
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <span className="text-3xl font-bold">{timeLeft}s</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Question Card */}
      <Card className="rounded-3xl bg-white/[0.08] dark:bg-zinc-800/50 backdrop-blur-xl border border-white/30 dark:border-zinc-700/50 shadow-[0_8px_24px_rgba(0,0,0,0.15)] dark:shadow-xl">
        <CardContent className="p-6">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-500 dark:text-zinc-400 uppercase tracking-wide">
                {currentQuestion.category}
              </span>
              <span className={`text-xs px-2 py-1 rounded-full ${
                currentQuestion.difficulty === 'easy' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                currentQuestion.difficulty === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' :
                'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
              }`}>
                {currentQuestion.difficulty}
              </span>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              {currentQuestion.text}
            </h3>
          </div>

          {/* Options */}
          <div className="space-y-3">
            {currentQuestion.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswer(option)}
                disabled={isAnswered}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${getAnswerClass(option)}`}
              >
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center font-bold text-gray-700">
                    {String.fromCharCode(65 + index)}
                  </span>
                  <span className="font-medium">{option}</span>
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Boosters Bar */}
      <Card className="rounded-3xl bg-white/[0.08] dark:bg-zinc-800/50 backdrop-blur-xl border border-white/30 dark:border-zinc-700/50 shadow-[0_8px_24px_rgba(0,0,0,0.15)] dark:shadow-xl">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-700 dark:text-white">Boosters Available:</p>
            <div className="flex gap-2">
              {boosters.map((booster) => (
                <Button
                  key={booster.id}
                  onClick={() => handleBooster(booster.id)}
                  disabled={!booster.available || usedBoosters.includes(booster.id) || isAnswered}
                  variant="outline"
                  size="sm"
                  className={`rounded-full ${
                    usedBoosters.includes(booster.id) 
                      ? 'opacity-50' 
                      : 'border-[#5B6FED] hover:bg-[#E8E8FF]'
                  }`}
                  title={booster.description}
                >
                  <span className="mr-1">{booster.icon}</span>
                  {booster.name}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

